import React from 'react';
import {
  Button,
  Card,
  Flex,
  Tag,
  Tooltip,
  Typography,
  theme,
} from '@duxton-web/ui';
import {
  colorBaseBlue50,
  colorBaseBlue400,
  colorBaseBlue600,
  colorStatusAlertSoftest,
  colorStatusPositiveSoftest,
} from '@duxton-web/design-tokens/themes/light';
import {
  ClockOutlined,
  ChevronRightUniversal,
  CloseCircleFilled,
  LaunchUniversal,
  TickMarkFilled,
  WarningFilled,
} from '@duxton-web/icons';
import {
  CAMPAIGN_TYPE_COLORS,
  getCampaignType,
  getCampaignUrl,
} from './debuggingToolsConstants';
import {
  displayStatusLabel,
  displayStatusTooltip,
  getCommsDisplayStatus,
  humanizeStatus,
} from './commsTraceStatus';
import type { CommsDisplayStatus } from './commsTraceStatus';
import {
  getJourneyChannel,
  getJourneyTemplateUrl,
  getMessageComponent,
} from './commsTracePresentation';
import {
  formatEventTime,
  getJourneyEventTimestamp,
} from './commsTraceTimeline';
import type { CommsTraceJourney } from './types';

const missingValue = <Typography.Text type="secondary">-</Typography.Text>;

const outcomeTagProps = (
  status: CommsDisplayStatus,
): { color: string; icon?: React.ReactNode } => {
  switch (status) {
    case 'sent':
    case 'received':
      return { color: 'success', icon: <TickMarkFilled /> };
    case 'failed':
      return { color: 'error', icon: <CloseCircleFilled /> };
    case 'unknown':
      return { color: 'warning', icon: <WarningFilled /> };
    default:
      return { color: 'default' };
  }
};

const ExternalLinkButton: React.FC<{ title: string; ariaLabel: string; href: string }> = ({
  title,
  ariaLabel,
  href,
}) => (
  <Tooltip title={title}>
    <Button
      type="text"
      size="small"
      shape="circle"
      icon={<LaunchUniversal />}
      aria-label={ariaLabel}
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    />
  </Tooltip>
);

const statusCardBackground = (
  status: CommsDisplayStatus,
): string => {
  switch (status) {
    case 'sent':
    case 'received':
      return colorStatusPositiveSoftest;
    case 'failed':
      return colorStatusAlertSoftest;
    case 'unknown':
      return '#fcf5cf';
    default:
      return 'transparent';
  }
};

type Props = {
  journey: CommsTraceJourney;
  timezone: string;
  timezoneCode?: string;
  onClick: () => void;
};

const CommsTraceTimelineCard: React.FC<Props> = ({
  journey,
  timezone,
  timezoneCode,
  onClick,
}) => {
  const { token } = theme.useToken();
  const message = getMessageComponent(journey);
  const channel = getJourneyChannel(journey);
  const timestamp = getJourneyEventTimestamp(journey);
  const formattedTime = formatEventTime(timestamp, timezone);
  const timeLabel = timezoneCode && formattedTime !== '-'
    ? `${formattedTime} ${timezoneCode}`
    : formattedTime;
  const title = message?.title || null;
  const content = message?.content || null;
  const hasMessage = Boolean(title || content);
  const bodyText = [title, content].filter(Boolean).join('\n');
  const templateUrl = getJourneyTemplateUrl(journey);

  const campaignName = journey.campaign?.name || journey.campaign?.id || journey.tracking_id;
  const campaignUrl = getCampaignUrl(
    journey.tracking_id,
    journey.campaign?.id,
    journey.campaign?.country_id,
  );
  const campaignType = getCampaignType(journey.tracking_id, journey.campaign?.is_trident);
  const displayStatus = getCommsDisplayStatus(journey);
  const statusTooltip = displayStatusTooltip(journey, displayStatus);
  const cardBackground = statusCardBackground(displayStatus);
  const isContentInjection = Boolean(journey.campaign?.is_attributepairing);
  const detailsContext = timeLabel !== '-'
    ? timeLabel
    : `tracking ${journey.tracking_id || 'unavailable'}`;

  return (
    <Card
      size="small"
      variant="borderless"
      style={{
        background: cardBackground,
        border: 'none',
        boxShadow: 'none',
        cursor: 'pointer',
        overflow: 'hidden',
      }}
      styles={{ body: { padding: 0, background: cardBackground } }}
      onClick={onClick}
    >
      <Flex
        gap={token.marginSM}
        align="flex-start"
        style={{ padding: `${token.paddingSM}px ${token.paddingMD}px` }}
      >
        <Flex
          align="center"
          gap={token.marginXXS}
          style={{ flexShrink: 0, minWidth: 100 }}
        >
          <ClockOutlined />
          <Typography.Text strong>{timeLabel}</Typography.Text>
        </Flex>

        <Flex vertical gap={token.marginXS} style={{ flex: 1, minWidth: 0 }}>
          {title && <Typography.Text strong>{title}</Typography.Text>}
          {content && (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{bodyText}</span>}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{ marginBottom: 0, whiteSpace: 'pre-line' }}
              >
                {content}
              </Typography.Paragraph>
            </Tooltip>
          )}
          {!hasMessage && (
            journey.template_id ? (
              <Flex align="center" gap={token.marginXXS}>
                <Typography.Text type="secondary">{journey.template_id}</Typography.Text>
                {templateUrl ? (
                  <ExternalLinkButton
                    title="Open template"
                    ariaLabel={`Open template ${journey.template_id}`}
                    href={templateUrl}
                  />
                ) : null}
              </Flex>
            ) : missingValue
          )}
          <Flex align="center" gap={token.marginXS} wrap="wrap">
            {campaignType && (
              <Tooltip title="Campaign type">
                <Tag bordered={true} color={CAMPAIGN_TYPE_COLORS[campaignType]}>
                  {campaignType}
                </Tag>
              </Tooltip>
            )}
            {channel && (
              <Tooltip title="Channel">
                <Tag bordered color="cyan">
                  {humanizeStatus(channel)}
                </Tag>
              </Tooltip>
            )}
            {isContentInjection && (
              <Tooltip title="Content injection">
                <Tag bordered color="orange">CI</Tag>
              </Tooltip>
            )}
            {campaignName ? (
              <Flex align="center" gap={token.marginXXS}>
                <Tooltip title="Campaign name">
                  <Tag
                    bordered={true}
                    style={{
                      background: colorBaseBlue50,
                      borderColor: colorBaseBlue400,
                      color: colorBaseBlue600,
                      whiteSpace: 'normal',
                    }}
                  >
                    {campaignName}
                  </Tag>
                </Tooltip>
                {campaignUrl ? (
                  <ExternalLinkButton
                    title="Open campaign"
                    ariaLabel={`Open campaign ${campaignName}`}
                    href={campaignUrl}
                  />
                ) : null}
              </Flex>
            ) : null}
          </Flex>
        </Flex>

        <Flex vertical align="flex-end" gap={token.marginXXS}>
          <Tooltip title={statusTooltip}>
            <Tag bordered {...outcomeTagProps(displayStatus)}>
              {displayStatusLabel(displayStatus)}
            </Tag>
          </Tooltip>
          <Tooltip title="Open details">
            <Button
              type="text"
              size="small"
              shape="circle"
              icon={<ChevronRightUniversal />}
              aria-label={`Open communication details: ${
                title || `template ${journey.template_id || 'unknown'}`
              }, ${detailsContext}`}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                onClick();
              }}
            />
          </Tooltip>
        </Flex>
      </Flex>
    </Card>
  );
};

export default CommsTraceTimelineCard;
